/**
 * Configurações do formulário › "Dados de contato" (2026-09-29).
 *
 * Ligado, a página pública pede nome, telefone e e-mail — cada um obrigatório
 * ou não — e a resposta fica ligada ao contato (achado pelo telefone ou
 * criado). Desligado, a resposta é anônima. O telefone ou o e-mail tem de ser
 * obrigatório: sem um dos dois não há como garantir o contato (o backend
 * recusa a mesma coisa).
 */
import React from 'react';
import type { FormContactDataSettings } from '@troia-v3/types';
import { Switch } from '@/shared/components/ui';

interface FormContactDataSectionProps {
  value: FormContactDataSettings;
  onChange: (value: FormContactDataSettings) => void;
}

export const FormContactDataSection: React.FC<FormContactDataSectionProps> = ({ value, onChange }) => {
  const set = (partial: Partial<FormContactDataSettings>) => onChange({ ...value, ...partial });
  // Não deixa desligar o último entre telefone e e-mail.
  const phoneTravado = value.requirePhone && !value.requireEmail;
  const emailTravado = value.requireEmail && !value.requirePhone;

  return (
    <div className="space-y-4" data-testid="form-contact-data-section">
      <Switch
        label="Exigir dados de contato"
        description={value.enabled
          ? 'A página pede os dados abaixo e cada resposta fica ligada ao contato de quem respondeu.'
          : 'A resposta é anônima: o relatório não mostra quem respondeu.'}
        checked={value.enabled}
        onChange={(enabled) => set({ enabled })}
        data-testid="form-contact-data-enabled"
      />
      {value.enabled && (
        <div className="space-y-3 pl-1" data-testid="form-contact-data-fields">
          <Switch
            label="Nome obrigatório"
            checked={value.requireName}
            onChange={(requireName) => set({ requireName })}
            size="sm"
            data-testid="form-contact-data-require-name"
          />
          <Switch
            label="Telefone obrigatório"
            checked={value.requirePhone}
            onChange={(requirePhone) => set({ requirePhone })}
            disabled={phoneTravado}
            size="sm"
            data-testid="form-contact-data-require-phone"
          />
          <Switch
            label="E-mail obrigatório"
            checked={value.requireEmail}
            onChange={(requireEmail) => set({ requireEmail })}
            disabled={emailTravado}
            size="sm"
            data-testid="form-contact-data-require-email"
          />
          <p className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>
            O telefone ou o e-mail precisa ser obrigatório — é por ele que achamos o contato. Quem já é contato é
            reconhecido pelo telefone, e o dado novo que ele informar é acrescentado ao cadastro.
          </p>
        </div>
      )}
    </div>
  );
};
